import type { FC } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { HolderOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { Paper, Checkbox, Typography, IconButton, Box, Chip, Tooltip } from '@mui/material'

interface RewardCardProps {
  reward: any
  isSelected: boolean
  onToggleSelect: (id: string) => void
  onEdit: (id: string) => void
  onDelete: (id: string) => void
}

export const RewardCard: FC<RewardCardProps> = ({ reward, isSelected, onToggleSelect, onEdit, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: reward.id!,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      {...attributes}
      elevation={0}
      variant="outlined"
      sx={{
        px: 1.5,
        py: 0.5,
        mb: 0.5,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        '&:hover': {
          backgroundColor: 'action.hover',
          boxShadow: 1,
        },
      }}
    >
      <Box {...listeners} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
        <HolderOutlined style={{ fontSize: 14, opacity: 0.5 }} />
      </Box>

      <Checkbox
        checked={isSelected}
        onChange={() => onToggleSelect(reward.id!)}
        onClick={(e) => e.stopPropagation()}
        size="small"
      />

      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
          {reward.name}
        </Typography>
      </Box>

      {reward.supportsEmployeeContribution && (
        <Tooltip title="Employee Contribution Allowed">
          <Chip label="EC" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }} />
        </Tooltip>
      )}

      <Box sx={{ display: 'flex', gap: 0.25 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            onEdit(reward.id!)
          }}
          sx={{ padding: 0.5 }}
        >
          <EditOutlined style={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(reward.id!)
          }}
          sx={{ padding: 0.5 }}
        >
          <DeleteOutlined style={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Paper>
  )
}
